<template>
  <div class="booking-widget-root booking-widget-wrapper" :style="bookingWidgetStyle">
    <div class="booking-widget">
      <div v-if="paymentReturnError" class="payment-return-error">
        <span>{{ paymentReturnError }}</span>
        <button type="button" class="payment-return-error-close" @click="paymentReturnError = ''">
          <img :src="closeIcon" alt="Закрыть" />
        </button>
      </div>
      <UserWelcome v-if="displayUser" :user="displayUser" @logout="handleLogout" />
      <button
        v-else-if="!isAuthorized"
        type="button"
        class="guest-login-button"
        @click="isAuthModalOpen = true"
      >
        <span>Войти в аккаунт</span>
        <img :src="loginIcon" alt="" class="guest-login-icon" />
      </button>
      <ClubInfo
        v-if="club"
        :club="club"
        :branches="branches"
        :selectedBranchId="club.id"
        :primary-color="bookingWidgetPrimaryColor"
        @change-branch="handleBranchChange"
      />
      <RentReservations
        ref="rentReservationsRef"
        :is-ready="isAuthorized && isAuthStateResolved && !!club"
        :currency="bookingCurrency"
        :primary-color="bookingWidgetPrimaryColor"
      />
      <DirectionFilters
        :directions="directions"
        v-model="selectedDirections"
        v-model:timeRanges="selectedTimeRanges"
        v-model:durations="selectedDurations"
        @reset-filters="handleFiltersReset"
      />
      <DatePicker
        :availableDates="visibleAvailableDatesStrings"
        v-model="selectedDate"
      />
      <TrainerToggle
        v-if="showWithTrainers && selectedDate"
        v-model="trainersEnabled"
      />
      <TrainersList
        v-if="showWithTrainers && selectedDate && trainers.length > 0 && trainersEnabled"
        :trainers="trainers"
        :currency="bookingCurrency"
        v-model="selectedTrainerId"
      />

      <TimeSlots
        v-if="selectedDate"
        :slots="filteredTimeSlots"
        :selectedDate="selectedDate"
        :selectedTrainerName="showWithTrainers ? selectedTrainerFullName : undefined"
        :currency="bookingCurrency"
        v-model="selectedSlotId"
      />

      <CourtsSelection
        v-if="selectedSlotId && courts.length > 0"
        :courts="filteredCourts"
        :currency="bookingCurrency"
        v-model="selectedCourtDuration"
        @book="handleBooking"
      />

      <BookingSuccessModal
        :isOpen="showThankYouModal"
        :date="successBooking.date"
        :time="successBooking.time"
        :courtName="successBooking.courtName"
        :duration="successBooking.duration"
        :trainerName="successBooking.trainerName"
        :paymentMethod="completedPaymentMethod"
        :paymentAmount="completedPaymentAmount"
        @close="closeThankYouModal"
      />

      <BookingPaymentModal
        :isOpen="isPaymentModalOpen"
        :bookingPayload="paymentBookingPayload"
        :user="user"
        :currency="bookingCurrency"
        @close="isPaymentModalOpen = false"
        @success="handlePaymentSuccess"
      />

      <AuthModal
        :isOpen="isAuthModalOpen"
        @close="handleAuthClose"
        @authorized="handleAuthorized"
      />

      <div
        v-if="isLogoutModalOpen"
        class="confirm-modal-overlay"
        @click.self="isLogoutModalOpen = false"
      >
        <div class="confirm-modal">
          <h3 class="confirm-modal-title">Вы действительно хотите выйти из аккаунта?</h3>
          <button
            type="button"
            class="confirm-modal-close"
            @click="isLogoutModalOpen = false"
          >
            <img :src="closeIcon" alt="Закрыть" class="confirm-modal-close-icon" />
          </button>
          <div class="confirm-modal-actions">
            <button
              type="button"
              class="confirm-modal-button confirm-modal-cancel"
              @click="isLogoutModalOpen = false"
            >
              Отмена
            </button>
            <button
              type="button"
              class="confirm-modal-button confirm-modal-submit"
              @click="confirmLogout"
            >
              Выйти
            </button>
          </div>
        </div>
      </div>
    </div>

    <BookingSummary
      :date="selectedDate"
      :time="selectedSlotTime"
      :courtName="selectedCourtName"
      :duration="selectedCourtDuration?.duration"
      :trainerName="showWithTrainers ? selectedTrainerFullName : undefined"
      :totalPrice="selectedCourtDuration?.price"
      :currency="selectedCourtDuration?.currency || bookingCurrency"
      :primary-color="bookingWidgetPrimaryColor"
      :secondary-color="bookingWidgetPrimaryColor"
      @book="handleBooking"
    />

    <div class="widget-footer">
      <a href="https://fitbase.io/?utm_source=widget&utm_campaign=booking" target="_blank" rel="noopener noreferrer" class="footer-link">
        <img :src="workOnFitbaseImage" alt="Работает на FITBASE" class="footer-image" />
      </a>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, watch, computed, ref } from 'vue'
import { useStore } from 'vuex'
import UserWelcome from '@/widgets/booking/components/UserWelcome.vue'
import ClubInfo from '@/widgets/booking/components/ClubInfo.vue'
import DirectionFilters from '@/widgets/booking/components/DirectionFilters.vue'
import DatePicker from '@/widgets/booking/components/DatePicker.vue'
import TrainerToggle from '@/widgets/booking/components/TrainerToggle.vue'
import TrainersList from '@/widgets/booking/components/TrainersList.vue'
import TimeSlots from '@/widgets/booking/components/TimeSlots.vue'
import CourtsSelection from '@/widgets/booking/components/CourtsSelection.vue'
import BookingSummary from '@/widgets/booking/components/BookingSummary.vue'
import BookingSuccessModal from '@/widgets/booking/components/BookingSuccessModal.vue'
import BookingPaymentModal from '@/widgets/booking/components/BookingPaymentModal.vue'
import AuthModal from '@/widgets/booking/components/AuthModal.vue'
import RentReservations from '@/widgets/booking/components/RentReservations.vue'
import { bookingWidgetFeatures } from '@/widgets/booking/config/features'
import { BookingAPI } from '@/widgets/booking/api'
import { pickPreferredCurrency } from '@/widgets/booking/utils/currency'
import { takePendingAtmosInvoiceId } from '@/utilities'
import loginIcon from '@/widgets/booking/assets/img/icons/booking-logout.svg'
import closeIcon from '@/widgets/booking/assets/img/icons/booking-close.svg'
import workOnFitbaseImage from '@/assets/img/work_on_fitbase.svg'
import {
  AUTH_CHANGE_EVENT,
  clearStoredAuthPhone,
  clearStoredAuthToken,
  getRuntimeConfig,
  getStoredAuthPhone,
  getStoredAuthToken,
  setRuntimeConfig,
  setStoredAuthPhone,
  setStoredAuthToken
} from '@/widgets/booking/config/runtime'
import { PAYMENT_SCOPE_BOOKING_WIDGET, takePendingPayment, waitForPaidInvoice } from '@/utilities'

const store = useStore()
const showWithTrainers = bookingWidgetFeatures.showWithTrainers
const isAuthModalOpen = ref(false)
const isLogoutModalOpen = ref(false)
const isPaymentModalOpen = ref(false)
const completedPaymentMethod = ref('card')
const completedPaymentAmount = ref(0)
const paidBookingSnapshot = ref(null)
const paymentReturnError = ref('')
const shouldOpenBookingAfterAuth = ref(false)
const initialDataLoadingCount = ref(0)
const authToken = ref(getStoredAuthToken() || '')
const authorizedPhone = ref(getStoredAuthPhone() || '')
const isAuthStateResolved = ref(!authToken.value)
const settings = ref(null)
const rentReservationsRef = ref(null)

// Computed properties from store state
const user = computed(() => store.state.user)
const club = computed(() => store.state.club)
const branches = computed(() => store.state.branches)
const directions = computed(() => store.state.directions)
const trainers = computed(() => store.state.trainers)
const timeSlots = computed(() => store.state.timeSlots)
const courts = computed(() => store.state.courts)
const showThankYouModal = computed(() => store.state.showThankYouModal)
const isAuthorized = computed(() => !!authToken.value)
const isLoadingInitialData = computed(() => initialDataLoadingCount.value > 0)
const bookingWidgetPrimaryColor = computed(() => settings.value?.bookingWidget?.primaryWidgetColor || '#3C67D6')
const displayUser = computed(() => {
  if (user.value) {
    return user.value
  }

  if (!isAuthorized.value) {
    return null
  }

  if (!authorizedPhone.value && !isAuthStateResolved.value) {
    return null
  }

  return {
    id: 0,
    name: authorizedPhone.value || 'User',
    avatar: ''
  }
})

// Computed properties from getters
const availableDatesStrings = computed(() => store.getters.availableDatesStrings)
const visibleAvailableDatesStrings = computed(() => {
  const countWeek = Number(settings.value?.bookingWidget?.countWeek || 0)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const maxDate = getBookingWindowEndDate(countWeek)

  return availableDatesStrings.value.filter((dateString) => {
    const [y, m, d] = dateString.split('-').map(Number)
    const date = new Date(y, m - 1, d)

    return date >= today && date <= maxDate
  })
})
const selectedTrainerFullName = computed(() => store.getters.selectedTrainerFullName)
const selectedSlotTime = computed(() => store.getters.selectedSlotTime)
const selectedCourtName = computed(() => store.getters.selectedCourtName)
const bookingCurrency = computed(() => pickPreferredCurrency(settings.value?.currency, club.value?.currency))
const selectedTimeRanges = ref([])
const selectedDurations = ref([])
const paymentBookingPayload = computed(() => {
  const courtDuration = selectedCourtDuration.value

  if (!selectedDate.value || !selectedSlotId.value || !courtDuration?.serviceId || !courtDuration.trainingId) {
    return null
  }

  const selectedSlot = timeSlots.value.find((slot) => slot.id === selectedSlotId.value)
  if (!selectedSlot) {
    return null
  }

  return {
    training_id: courtDuration.trainingId,
    place_id: courtDuration.courtId,
    service_id: courtDuration.serviceId,
    date: selectedDate.value,
    time: selectedSlot.time,
    trainer_id: showWithTrainers && trainersEnabled.value ? selectedTrainerId.value : undefined,
    price: courtDuration.price,
    currency: courtDuration.currency || bookingCurrency.value,
    serviceId: courtDuration.serviceId,
    courtName: selectedCourtName.value,
    duration: courtDuration.duration,
    trainerName: showWithTrainers && trainersEnabled.value ? selectedTrainerFullName.value : undefined
  }
})

const successBooking = computed(() => paidBookingSnapshot.value || {
  date: selectedDate.value,
  time: selectedSlotTime.value,
  courtName: selectedCourtName.value,
  duration: selectedCourtDuration.value?.duration,
  trainerName: showWithTrainers ? selectedTrainerFullName.value : undefined
})

// Two-way computed properties for v-model bindings
const selectedDirections = computed({
  get: () => store.state.selectedDirections,
  set: (value) => store.commit('SET_SELECTED_DIRECTIONS', value)
})

const selectedDate = computed({
  get: () => store.state.selectedDate,
  set: (value) => store.commit('SET_SELECTED_DATE', value)
})

const selectedTrainerId = computed({
  get: () => store.state.selectedTrainerId,
  set: (value) => store.commit('SET_SELECTED_TRAINER_ID', value)
})

const trainersEnabled = computed({
  get: () => store.state.trainersEnabled,
  set: (value) => store.commit('SET_TRAINERS_ENABLED', value)
})

const selectedSlotId = computed({
  get: () => store.state.selectedSlotId,
  set: (value) => store.commit('SET_SELECTED_SLOT_ID', value)
})

const selectedCourtDuration = computed({
  get: () => store.state.selectedCourtDuration,
  set: (value) => store.commit('SET_SELECTED_COURT_DURATION', value)
})

const timeRangeBounds = {
  morning: [8 * 60, 12 * 60],
  day: [12 * 60, 17 * 60],
  evening: [17 * 60, 22 * 60]
}

const filteredTimeSlots = computed(() => {
  const slots = timeSlots.value

  if (selectedTimeRanges.value.length === 0) {
    return slots
  }

  return slots.filter((slot) => {
    const [hours, minutes] = slot.time.split(':').map(Number)
    const totalMinutes = hours * 60 + minutes

    return selectedTimeRanges.value.some((rangeId) => {
      const bounds = timeRangeBounds[rangeId]
      return bounds && totalMinutes >= bounds[0] && totalMinutes < bounds[1]
    })
  })
})

const filteredCourts = computed(() => {
  if (selectedDurations.value.length === 0) {
    return courts.value
  }

  const allowed = selectedDurations.value.map(Number)

  return courts.value
    .map((court) => ({
      ...court,
      durations: court.durations.filter((duration) => allowed.includes(Number(duration.minutes)))
    }))
    .filter((court) => court.durations.length > 0)
})

const bookingWidgetStyle = computed(() => {
  const primaryColor = bookingWidgetPrimaryColor.value

  return {
    '--booking-widget-primary-color': primaryColor,
    '--booking-widget-primary-color-soft': `${primaryColor}1A`
  }
})

function formatDate(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function getBookingWindowEndDate(countWeek) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  if (countWeek > 0) {
    const endOfWeek = new Date(today)
    const day = endOfWeek.getDay()
    const daysUntilSunday = day === 0 ? 0 : 7 - day
    endOfWeek.setDate(endOfWeek.getDate() + daysUntilSunday + (countWeek - 1) * 7)
    return endOfWeek
  }

  return new Date(today.getFullYear(), 11, 31)
}

function syncWidgetSettings(nextSettings) {
  settings.value = nextSettings
  store.commit('SET_BOOKING_WIDGET_COUNT_WEEK', nextSettings?.bookingWidget?.countWeek || 0)
}

// Load initial data
onMounted(async () => {
  isAuthStateResolved.value = !authToken.value
  await syncCurrentClub()
  syncWidgetSettings(await BookingAPI.getSettings())
  await loadInitialBookingData()
  isAuthStateResolved.value = true
  window.addEventListener(AUTH_CHANGE_EVENT, syncAuthState)
  window.addEventListener('storage', syncAuthState)
  await resumePendingAtmosPayment()
  await showPendingPaymentResult()
})

async function resumePendingAtmosPayment() {
  if (window.opener) {
    return
  }

  const invoiceId = takePendingAtmosInvoiceId()
  if (!invoiceId) {
    return
  }

  const result = await BookingAPI.checkInvoice({ save_card: 1, invoiceId })
  if (result && result.data && result.data.status === 'success') {
    handlePaymentSuccess({ paymentMethod: 'card', paymentAmount: 0 })
  }
}

onBeforeUnmount(() => {
  window.removeEventListener(AUTH_CHANGE_EVENT, syncAuthState)
  window.removeEventListener('storage', syncAuthState)
})

// Watch for changes and dispatch actions
watch(selectedDirections, () => {
  if (isLoadingInitialData.value) {
    return
  }

  store.dispatch('onDirectionsChange')
})

watch(selectedDate, () => {
  if (isLoadingInitialData.value) {
    return
  }

  store.dispatch('onDateChange')
})

watch(selectedTrainerId, () => {
  if (isLoadingInitialData.value) {
    return
  }

  if (showWithTrainers) {
    store.dispatch('onTrainerChange')
  }
})

watch(selectedSlotId, () => {
  if (isLoadingInitialData.value) {
    return
  }

  store.dispatch('onSlotChange')
})

watch(filteredTimeSlots, (slots) => {
  if (isLoadingInitialData.value) {
    return
  }

  const hasSelectedSlot = slots.some((slot) => slot.id === store.state.selectedSlotId)

  if (!hasSelectedSlot && store.state.selectedSlotId !== undefined) {
    store.commit('SET_SELECTED_SLOT_ID', undefined)
    store.commit('SET_SELECTED_COURT_DURATION', undefined)
    store.commit('SET_COURTS', [])
  }
})

watch(filteredCourts, (items) => {
  if (isLoadingInitialData.value) {
    return
  }

  const selection = store.state.selectedCourtDuration
  if (!selection) {
    return
  }

  const hasSelectedDuration = items.some((court) =>
    court.id === selection.courtId && court.durations.some((duration) => duration.minutes === selection.duration)
  )

  if (!hasSelectedDuration) {
    store.commit('SET_SELECTED_COURT_DURATION', undefined)
  }
})

function handleFiltersReset() {
  store.commit('SET_SELECTED_DATE', formatDate(new Date()))
  store.commit('SET_SELECTED_SLOT_ID', undefined)
  store.commit('SET_SELECTED_COURT_DURATION', undefined)
  store.commit('SET_COURTS', [])
}

async function loadInitialBookingData() {
  initialDataLoadingCount.value += 1

  try {
    await store.dispatch('loadInitialData')
  } finally {
    initialDataLoadingCount.value = Math.max(0, initialDataLoadingCount.value - 1)
  }
}

// Handle booking
function handleBooking() {
  if (!isAuthorized.value) {
    shouldOpenBookingAfterAuth.value = true
    isAuthModalOpen.value = true
    return
  }

  if (!paymentBookingPayload.value) {
    return
  }

  isPaymentModalOpen.value = true
}

function handleAuthClose() {
  isAuthModalOpen.value = false
  shouldOpenBookingAfterAuth.value = false
}

function continuePendingBookingAfterAuth() {
  if (!shouldOpenBookingAfterAuth.value) {
    return
  }

  shouldOpenBookingAfterAuth.value = false

  if (!paymentBookingPayload.value) {
    return
  }

  isPaymentModalOpen.value = true
}

/**
 * Оплата на странице эквайринга уводит клиента со страницы клуба и возвращает обратно
 * уже перезагруженной. Счёт закрывает вебхук, поэтому итог оплаты узнаём опросом счёта
 */
async function showPendingPaymentResult() {
  const pending = takePendingPayment(PAYMENT_SCOPE_BOOKING_WIDGET)
  if (!pending) {
    return
  }

  const paid = await waitForPaidInvoice(
    () => BookingAPI.checkInvoice({ save_card: 1, invoiceId: pending.invoiceId })
  )

  if (!paid) {
    paymentReturnError.value = 'Оплата не прошла. Попробуйте забронировать ещё раз.'
    return
  }

  const booking = pending.booking || {}
  paidBookingSnapshot.value = booking
  completedPaymentMethod.value = booking.paymentMethod || 'card'
  completedPaymentAmount.value = Number(booking.paymentAmount || 0)
  store.commit('SET_SHOW_THANK_YOU_MODAL', true)
  await loadInitialBookingData()
}

// Close modal
function closeThankYouModal() {
  paidBookingSnapshot.value = null
  store.dispatch('closeThankYouModal')
}

async function handleAuthorized(payload) {
  setStoredAuthToken(payload.token)
  setStoredAuthPhone(payload.phone)
  authToken.value = payload.token
  authorizedPhone.value = payload.phone
  isAuthStateResolved.value = true
  isAuthModalOpen.value = false
  const [, nextSettings] = await Promise.all([
    syncCurrentClub(),
    BookingAPI.getSettings()
  ])
  syncWidgetSettings(nextSettings)
  await loadInitialBookingData()
  continuePendingBookingAfterAuth()
}

function handleLogout() {
  isLogoutModalOpen.value = true
}

function confirmLogout() {
  clearStoredAuthToken()
  clearStoredAuthPhone()
  store.commit('SET_USER', null)
  authToken.value = ''
  authorizedPhone.value = ''
  isAuthStateResolved.value = true
  isLogoutModalOpen.value = false
}

async function handleBranchChange(branchId) {
  if (!branchId || branchId === club.value?.id) {
    return
  }

  setRuntimeConfig({ clubId: branchId, filialId: branchId })
  await syncCurrentClub(branchId)
  syncWidgetSettings(await BookingAPI.getSettings())
  store.commit('RESET_SELECTIONS')
  store.commit('SET_AVAILABLE_DAYS', [])
  store.commit('SET_TIME_SLOTS', [])
  store.commit('SET_COURTS', [])
  await loadInitialBookingData()
}

async function syncAuthState() {
  const nextToken = getStoredAuthToken() || ''
  const nextPhone = getStoredAuthPhone() || ''

  if (authToken.value === nextToken && authorizedPhone.value === nextPhone) {
    return
  }

  authToken.value = nextToken
  authorizedPhone.value = nextPhone

  if (nextToken) {
    isAuthStateResolved.value = false
    await syncCurrentClub()
    syncWidgetSettings(await BookingAPI.getSettings())
    await loadInitialBookingData()
    isAuthStateResolved.value = true
    return
  }

  store.commit('SET_USER', null)
  isAuthStateResolved.value = true
}

function handlePaymentSuccess(payload = {}) {
  completedPaymentMethod.value = payload.paymentMethod || 'card'
  completedPaymentAmount.value = Number(payload.paymentAmount || 0)
  isPaymentModalOpen.value = false
  store.commit('SET_SHOW_THANK_YOU_MODAL', true)
  rentReservationsRef.value?.reload()
}

async function syncCurrentClub(clubId = undefined) {
  const targetClubId = clubId || getRuntimeConfig().clubId

  if (!authToken.value || !targetClubId) {
    return
  }

  await BookingAPI.changeCurrentClub(targetClubId)
}
</script>

<style lang="scss">
@import '@/widgets/booking/assets/scss/global.scss';

.booking-widget-wrapper {
  max-width: 500px;
  margin: 20px auto 0;
  display: flex;
  flex-direction: column;
  position: relative;
}

.booking-widget {
  padding: $spacing-lg;
  background: white;
  border-radius: 30px;
  flex: 1;
}

.guest-login-button {
  width: fit-content;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  margin-bottom: 16px;
  padding: 0 14px;
  border: none;
  border-radius: 12px;
  background: var(--booking-widget-primary-color-soft, #3C67D61A);
  background-color: var(--booking-widget-primary-color-soft, #3C67D61A);
  color: var(--booking-widget-primary-color, #3C67D6);
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  cursor: pointer;
  transition: background 0.2s ease, opacity 0.2s ease;

  &:hover {
    background: rgba(60, 103, 214, 0.16);
    background-color: rgba(60, 103, 214, 0.16);
  }
}

.guest-login-icon {
  width: 16px;
  height: 16px;
  display: block;
}

.payment-return-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(232, 74, 74, 0.1);
  color: #E84A4A;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  line-height: 20px;
}

.payment-return-error-close {
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;

  img {
    width: 14px;
    height: 14px;
    display: block;
  }
}

.confirm-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.5);
}

.confirm-modal {
  position: relative;
  width: 392px;
  max-width: 100%;
  box-sizing: border-box;
  padding: 24px 20px 20px;
  background: #FFFFFF;
  border-radius: 24px;
}

.confirm-modal-title {
  width: 328px;
  max-width: 100%;
  margin: 0 0 24px;
  color: #252525;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
  text-align: center;
}

.confirm-modal-close {
  position: absolute;
  top: 24px;
  right: 20px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 7px;
  background: #F3F5F8;
  cursor: pointer;
}

.confirm-modal-close-icon {
  width: 14px;
  height: 14px;
  display: block;
}

.confirm-modal-actions {
  display: flex;
  gap: 8px;
}

.confirm-modal-button {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0 12px;
  box-sizing: border-box;
  appearance: none;
  -webkit-appearance: none;
  border: none;
  border-radius: 12px;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.2s ease, opacity 0.2s ease;
}

.confirm-modal-cancel {
  background: var(--booking-widget-primary-color-soft, #3C67D61A);
  background-color: var(--booking-widget-primary-color-soft, #3C67D61A) !important;
  color: var(--booking-widget-primary-color, #3C67D6);

  &:hover {
    opacity: 0.9;
  }
}

.confirm-modal-submit {
  background: #F15F941A;
  background-color: #F15F941A !important;
  color: #ED3D3D;

  &:hover {
    background: rgba(241, 95, 148, 0.18);
    background-color: rgba(241, 95, 148, 0.18) !important;
  }
}

body {
  background: #F3F5F8;
}

.widget-footer {
  margin-top: 30px;
  text-align: center;
}

.footer-link {
  display: inline-block;
  line-height: 0;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
}

.footer-image {
  display: block;
  height: 32px;
  width: auto;
}
</style>
